/**
 * 計助ダッシュボード: 2要因ANOVA用 ブロックフォーム
 * - 1つ目の要素（因子A）: 列に表示（列ヘッダーで編集可能）
 * - 2つ目の要素（因子B）: 行に表示（各行の左端を行ヘッダーとして編集可能）
 * - 1セルあたりのデータ数 n を入力。各 (因子A水準 × 因子B水準) セルに n 個の値（行で展開）
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  twoWayBlockFormToGridData,
  gridDataToTwoWayBlockForm,
  type TwoWayBlockForm,
  normalizeKeisukeNumericInput,
} from '../../../../utils/keisukeInputFormUtils';
import type { DataRow } from '../../../../stores/systems/keisuke/dashboardStore';
import { clampKeisukeOneWayGroupCount } from '../../../../stores/systems/keisuke/dashboardStore';
import {
  clipboardLooksLikeSpreadsheetGrid,
  clipboardPlainTextFromEvent,
  parseClipboardTsvMatrix,
} from './keisukeDashboardClipboardUtils';
import { useDebouncedFormApply } from './useDebouncedFormApply';
import styles from './KeisukeDashboardCenterPane.module.scss';
import grid from './KeisukeUnifiedDataGrid.module.scss';
import KeisukeClipboardHintBar from './KeisukeClipboardHintBar';
import KeisukeDashboardGridObservationCountRow from './KeisukeDashboardGridObservationCountRow';
import {
  clampKeisukeDashboardDataColWidthPx,
  KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX,
} from './keisukeDashboardDataGridLayout';

const DEFAULT_N_PER_CELL = 3;
const FACTOR_A = 'FactorA';
const FACTOR_B = 'FactorB';
const VALUE_COL = 'Value';

const TWO_WAY_META_MIN_PX = 56;
const TWO_WAY_META_MAX_PX = 320;
/** 因子Aの各データ列（t検定データ列と同じ初期幅） */

/** blocks から因子A・因子Bの水準リストを出現順に取得 */
function getOrderedLevels(blocks: TwoWayBlockForm['blocks']): { rowLabels: string[]; columnLabels: string[] } {
  const rowLabels: string[] = [];
  const columnLabels: string[] = [];
  blocks.forEach((b) => {
    if (b.factorBLabel !== undefined && b.factorBLabel !== null && !rowLabels.includes(b.factorBLabel)) {
      rowLabels.push(b.factorBLabel);
    }
    if (b.factorALabel !== undefined && b.factorALabel !== null && !columnLabels.includes(b.factorALabel)) {
      columnLabels.push(b.factorALabel);
    }
  });
  return { rowLabels, columnLabels };
}

/** (因子B, 因子A) に対応するブロックを取得。なければ null */
function getBlock(
  blocks: TwoWayBlockForm['blocks'],
  rowLabel: string,
  columnLabel: string
): { factorALabel: string; factorBLabel: string; values: string[] } | undefined {
  return blocks.find(
    (b) => String(b.factorBLabel ?? '') === String(rowLabel) && String(b.factorALabel ?? '') === String(columnLabel)
  );
}

function resizeArray<T>(arr: T[], len: number, fill: T = '' as T): T[] {
  if (arr.length === len) return arr;
  if (arr.length > len) return arr.slice(0, len);
  return [...arr, ...Array(len - arr.length).fill(fill)];
}

/** 因子A・Bの水準数（列・行）と n に合わせてブロックを組み直す。既存ラベル・値はインデックスが一致する範囲で維持 */
function rebuildTwoWayBlocksForCounts(
  prev: TwoWayBlockForm,
  nA: number,
  nB: number,
  n: number
): TwoWayBlockForm {
  const clampedA = clampKeisukeOneWayGroupCount(nA);
  const clampedB = clampKeisukeOneWayGroupCount(nB);
  const { rowLabels: oldR, columnLabels: oldC } = getOrderedLevels(prev.blocks);
  const colNames: string[] = [];
  for (let i = 0; i < clampedA; i++) {
    const prevL = oldC[i];
    colNames.push(prevL != null && String(prevL).trim() !== '' ? String(prevL) : `C${i + 1}`);
  }
  const rowNames: string[] = [];
  for (let j = 0; j < clampedB; j++) {
    const prevL = oldR[j];
    rowNames.push(prevL != null && String(prevL).trim() !== '' ? String(prevL) : `R${j + 1}`);
  }
  const blocks: TwoWayBlockForm['blocks'] = [];
  for (const bLabel of rowNames) {
    for (const aLabel of colNames) {
      const existing = prev.blocks.find(
        (x) =>
          String(x.factorBLabel ?? '') === String(bLabel) && String(x.factorALabel ?? '') === String(aLabel)
      );
      blocks.push({
        factorALabel: aLabel,
        factorBLabel: bLabel,
        values: existing ? resizeArray([...existing.values], n, '') : (Array(n).fill('') as string[]),
      });
    }
  }
  return {
    ...prev,
    nPerCell: n,
    blocks,
  };
}

function mergeTwoWayDataPaste(
  prev: TwoWayBlockForm,
  n: number,
  startR: number,
  startAIdx: number,
  matrix: string[][]
): TwoWayBlockForm {
  let blocks = prev.blocks.map((b) => ({
    ...b,
    values: resizeArray([...b.values], n, ''),
  }));
  const recalc = () => getOrderedLevels(blocks);

  /** 空文字は getOrderedLevels で1列にまとまるため、貼り付けで列を増やすときは一意の仮ラベルを使う */
  const ensureColumnCount = (minCols: number) => {
    let { columnLabels: cl } = recalc();
    while (cl.length < minCols) {
      const { rowLabels: rl } = recalc();
      const newA = `C${cl.length + 1}`;
      const nextBlocks =
        rl.length > 0
          ? rl.map((b) => ({
              factorALabel: newA,
              factorBLabel: b,
              values: Array(n).fill('') as string[],
            }))
          : [{ factorALabel: newA, factorBLabel: '', values: Array(n).fill('') as string[] }];
      blocks = [...blocks, ...nextBlocks];
      cl = recalc().columnLabels;
    }
  };

  const ensureRowCountForLastIndex = (lastRowIndexInclusive: number) => {
    const maxExpand = 256;
    for (let iter = 0; iter < maxExpand; iter++) {
      const rl = recalc().rowLabels;
      const total = rl.length * n;
      if (total > lastRowIndexInclusive) break;
      const cl = recalc().columnLabels;
      const newB = `R${rl.length + 1}`;
      const nextBlocks =
        cl.length > 0
          ? cl.map((a) => ({
              factorALabel: a,
              factorBLabel: newB,
              values: Array(n).fill('') as string[],
            }))
          : [{ factorALabel: 'C1', factorBLabel: newB, values: Array(n).fill('') as string[] }];
      blocks = [...blocks, ...nextBlocks];
    }
  };

  if (matrix.length === 0) return { ...prev, blocks };

  const lastC = matrix.reduce((m, line) => Math.max(m, startAIdx + (line?.length ?? 0) - 1), startAIdx);
  const lastR = startR + matrix.length - 1;
  ensureColumnCount(lastC + 1);
  ensureRowCountForLastIndex(lastR);

  const { rowLabels, columnLabels } = recalc();

  for (let i = 0; i < matrix.length; i++) {
    const r = startR + i;
    const line = matrix[i] ?? [];
    const bIdx = Math.floor(r / n);
    const rep = r % n;
    const rowLabel = rowLabels[bIdx] ?? '';
    for (let j = 0; j < line.length; j++) {
      const aIdx = startAIdx + j;
      const columnLabel = columnLabels[aIdx] ?? '';
      const val = normalizeKeisukeNumericInput(String(line[j] ?? ''));
      const existingIdx = blocks.findIndex(
        (b) =>
          String(b.factorBLabel ?? '') === String(rowLabel) && String(b.factorALabel ?? '') === String(columnLabel)
      );
      if (existingIdx >= 0) {
        const vals = resizeArray([...blocks[existingIdx]!.values], n, '');
        vals[rep] = val;
        blocks[existingIdx] = { ...blocks[existingIdx]!, values: vals };
      } else {
        const vals = Array(n).fill('') as string[];
        vals[rep] = val;
        blocks.push({ factorALabel: columnLabel, factorBLabel: rowLabel, values: vals });
      }
    }
  }

  return { ...prev, blocks };
}

type TwoWayDataGridCell = { r: number; aIdx: number };
type TwoWayDataGridSelection = { a: TwoWayDataGridCell; b: TwoWayDataGridCell };

function normalizeTwoWayDataGridSelection(s: TwoWayDataGridSelection) {
  return {
    minR: Math.min(s.a.r, s.b.r),
    maxR: Math.max(s.a.r, s.b.r),
    minA: Math.min(s.a.aIdx, s.b.aIdx),
    maxA: Math.max(s.a.aIdx, s.b.aIdx),
  };
}

function twoWayDataGridSelectionCellCount(n: ReturnType<typeof normalizeTwoWayDataGridSelection>) {
  return (n.maxR - n.minR + 1) * (n.maxA - n.minA + 1);
}

function twoWayDataCellInSelection(r: number, aIdx: number, sel: TwoWayDataGridSelection | null) {
  if (!sel) return false;
  const x = normalizeTwoWayDataGridSelection(sel);
  return r >= x.minR && r <= x.maxR && aIdx >= x.minA && aIdx <= x.maxA;
}

function twoWayDataCellFromTarget(target: EventTarget | null): TwoWayDataGridCell | null {
  const el = target as HTMLElement | null;
  if (!el?.closest) return null;
  if (el.closest('[data-tw-ignore-selection="1"]')) return null;
  const inp = el.closest('input[data-tw-r]') as HTMLInputElement | null;
  if (!inp) return null;
  const rs = inp.getAttribute('data-tw-r');
  const as = inp.getAttribute('data-tw-a');
  if (rs == null || as == null) return null;
  return { r: parseInt(rs, 10), aIdx: parseInt(as, 10) };
}

function getTwoWayDataPasteAnchor(sel: TwoWayDataGridSelection | null, focus: TwoWayDataGridCell): TwoWayDataGridCell {
  if (!sel) return focus;
  const n = normalizeTwoWayDataGridSelection(sel);
  if (twoWayDataGridSelectionCellCount(n) <= 1) return focus;
  return { r: n.minR, aIdx: n.minA };
}

function buildTsvFromTwoWayDataSelection(
  f: TwoWayBlockForm,
  rowLabels: string[],
  columnLabels: string[],
  n: number,
  norm: ReturnType<typeof normalizeTwoWayDataGridSelection>
): string {
  const lines: string[][] = [];
  for (let r = norm.minR; r <= norm.maxR; r++) {
    const line: string[] = [];
    const bIdx = Math.floor(r / n);
    const rep = r % n;
    const rowLabel = rowLabels[bIdx] ?? '';
    for (let aIdx = norm.minA; aIdx <= norm.maxA; aIdx++) {
      const columnLabel = columnLabels[aIdx] ?? '';
      const block = getBlock(f.blocks, rowLabel, columnLabel);
      line.push(String(block?.values[rep] ?? ''));
    }
    lines.push(line);
  }
  return lines.map((ln) => ln.join('\t')).join('\n');
}

export interface KeisukeDashboardDataFormTwoWayBlocksProps {
  gridData: DataRow[];
  gridHeaders: string[];
  factorAColumn?: string;
  factorBColumn?: string;
  valueColumn?: string;
  nPerCell?: number;
  /** サンプル選択が変わったときグリッド同期の抑制を解除する（途中で表が空になるのを防ぐ） */
  syncResetToken?: string | null;
  onApply: (data: DataRow[], headers: string[]) => void;
  onNPerCellChange?: (n: number) => void;
  /** 因子Aの水準数（列数）。ストア `twoWayNLevelsA` */
  nLevelsA: number;
  onNLevelsAChange: (n: number) => void;
  /** 因子Bの水準数（行ブロック数）。ストア `twoWayNLevelsB` */
  nLevelsB: number;
  onNLevelsBChange: (n: number) => void;
  disabled?: boolean;
  /** 因子Aデータ列の幅を他形式と共有（px）。中央ペイン `longFormatTwoColWidthPx` */
  linkedDataColumnWidthPx?: number;
  onLinkedDataColumnWidthCommit?: (px: number) => void;
}

/** ストアから戻った grid とローカル form が同じ内容なら setForm しない（デバウンス↔同期のループ防止） */
function twoWayFormStateEqual(a: TwoWayBlockForm, b: TwoWayBlockForm): boolean {
  if (a.nPerCell !== b.nPerCell) return false;
  if (
    a.factorAColumn !== b.factorAColumn ||
    a.factorBColumn !== b.factorBColumn ||
    a.valueColumn !== b.valueColumn
  ) {
    return false;
  }
  if (a.blocks.length !== b.blocks.length) return false;
  for (let i = 0; i < a.blocks.length; i++) {
    const x = a.blocks[i];
    const y = b.blocks[i];
    if (String(x.factorALabel ?? '') !== String(y.factorALabel ?? '')) return false;
    if (String(x.factorBLabel ?? '') !== String(y.factorBLabel ?? '')) return false;
    if (x.values.length !== y.values.length) return false;
    for (let j = 0; j < x.values.length; j++) {
      if (String(x.values[j] ?? '') !== String(y.values[j] ?? '')) return false;
    }
  }
  return true;
}

function inferNPerCell(data: DataRow[], factorACol: string, factorBCol: string): number {
  const key = (a: string, b: string) => `${a}\t${b}`;
  const counts = new Map<string, number>();
  data.forEach((r) => {
    const a = String(r[factorACol] ?? '').trim();
    const b = String(r[factorBCol] ?? '').trim();
    if (!a || !b) return;
    const k = key(a, b);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  });
  const vals = [...counts.values()];
  if (vals.length === 0) return DEFAULT_N_PER_CELL;
  return Math.min(...vals) || DEFAULT_N_PER_CELL;
}

/**
 * マッピング未設定時は長形式の列順を優先（デフォルト FactorA だけだと group/value 2列データと不一致になる）
 */
function resolveTwoWayColumnNames(
  gridHeaders: string[],
  factorAColumn?: string,
  factorBColumn?: string,
  valueColumn?: string
): { factorA: string; factorB: string; valueCol: string } {
  const valueCol =
    valueColumn ??
    (gridHeaders.length >= 3
      ? gridHeaders[2]!
      : gridHeaders.length >= 2
        ? gridHeaders[1]!
        : VALUE_COL);
  const factorA = factorAColumn ?? gridHeaders[0] ?? FACTOR_A;
  const factorB =
    factorBColumn ?? (gridHeaders.length >= 3 ? gridHeaders[1]! : FACTOR_B);
  return { factorA, factorB, valueCol };
}

export default function KeisukeDashboardDataFormTwoWayBlocks({
  gridData,
  gridHeaders,
  factorAColumn,
  factorBColumn,
  valueColumn,
  nPerCell: initialN,
  syncResetToken,
  onApply,
  onNPerCellChange,
  nLevelsA,
  onNLevelsAChange,
  nLevelsB,
  onNLevelsBChange,
  disabled = false,
  linkedDataColumnWidthPx,
  onLinkedDataColumnWidthCommit,
}: KeisukeDashboardDataFormTwoWayBlocksProps) {
  const { t } = useTranslation();
  const fieldIdPrefix = useId();
  /** 表示・編集はローカル state を正とする（initialN ?? local だとストア値が常に勝ち、セルあたり件数が変えられない） */
  const [nPerCell, setNPerCellState] = useState(() => {
    const { factorA, factorB } = resolveTwoWayColumnNames(
      gridHeaders,
      factorAColumn,
      factorBColumn,
      valueColumn
    );
    return initialN ?? inferNPerCell(gridData, factorA, factorB);
  });
  /** applyForm → onApply でストア更新した直後は、gridData 由来の useEffect で form を上書きしない（2要因選択時の点滅ループ防止） */
  const skipSyncFromGridRef = useRef(false);
  /** applyForm 実行後しばらくはストアからの grid 同期を無視（デバウンス反映後の round-trip で setForm しない） */
  const suppressGridSyncUntilRef = useRef(0);
  const twoWayColDragRef = useRef<{
    kind: 'meta' | 'factorA';
    startX: number;
    startW: number;
  } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const gridDataAnchorRef = useRef<TwoWayDataGridCell | null>(null);
  const [gridDataSelection, setGridDataSelection] = useState<TwoWayDataGridSelection | null>(null);
  const [isDataDragSelecting, setIsDataDragSelecting] = useState(false);
  const [twoWayMetaColWidthPx, setTwoWayMetaColWidthPx] = useState(
    KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX
  );
  const [twoWayFactorAColWidthPx, setTwoWayFactorAColWidthPx] = useState(() =>
    clampKeisukeDashboardDataColWidthPx(
      linkedDataColumnWidthPx ?? KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX
    )
  );

  const [form, setForm] = useState<TwoWayBlockForm>(() => {
    const { factorA, factorB, valueCol } = resolveTwoWayColumnNames(
      gridHeaders,
      factorAColumn,
      factorBColumn,
      valueColumn
    );
    const n = initialN ?? inferNPerCell(gridData, factorA, factorB);
    const fromGrid = gridDataToTwoWayBlockForm(gridData, gridHeaders, factorA, factorB, valueCol, n);
    if (fromGrid) return fromGrid;
    return rebuildTwoWayBlocksForCounts(
      {
        nPerCell: n,
        factorAColumn: factorA,
        factorBColumn: factorB,
        valueColumn: valueCol,
        blocks: [],
      },
      nLevelsA,
      nLevelsB,
      n
    );
  });

  const prevLevelsRef = useRef({ a: nLevelsA, b: nLevelsB });
  useEffect(() => {
    if (prevLevelsRef.current.a === nLevelsA && prevLevelsRef.current.b === nLevelsB) return;
    prevLevelsRef.current = { a: nLevelsA, b: nLevelsB };
    setForm((prev) => rebuildTwoWayBlocksForCounts(prev, nLevelsA, nLevelsB, nPerCell));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nPerCell はセルあたり件数スピナー・initialN で別途リサイズ
  }, [nLevelsA, nLevelsB]);

  useEffect(() => {
    if (linkedDataColumnWidthPx == null) return;
    const next = clampKeisukeDashboardDataColWidthPx(linkedDataColumnWidthPx);
    setTwoWayFactorAColWidthPx((prev) => (prev === next ? prev : next));
  }, [linkedDataColumnWidthPx]);

  /** サンプル読込などでストアの twoWayNPerCell が変わったときだけローカルに反映（ブロックの values 長も合わせる） */
  useEffect(() => {
    if (initialN === undefined) return;
    setNPerCellState(initialN);
    setForm((prev) => ({
      ...prev,
      nPerCell: initialN,
      blocks: prev.blocks.map((b) => ({
        ...b,
        values: resizeArray([...b.values], initialN, ''),
      })),
    }));
  }, [initialN]);

  useEffect(() => {
    if (syncResetToken === undefined || syncResetToken === null) return;
    suppressGridSyncUntilRef.current = 0;
    skipSyncFromGridRef.current = false;
  }, [syncResetToken]);

  useEffect(() => {
    return () => {
      twoWayColDragRef.current = null;
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };
  }, []);

  useEffect(() => {
    if (!isDataDragSelecting) return;
    const onMove = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = twoWayDataCellFromTarget(el);
      if (cell && gridDataAnchorRef.current) {
        setGridDataSelection({ a: gridDataAnchorRef.current, b: cell });
      }
    };
    const onUp = () => setIsDataDragSelecting(false);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onUp, true);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };
  }, [isDataDragSelecting]);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current?.contains(t)) return;
      setGridDataSelection(null);
      gridDataAnchorRef.current = null;
    };
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, []);

  useEffect(() => {
    if (Date.now() < suppressGridSyncUntilRef.current) {
      return;
    }
    if (skipSyncFromGridRef.current) {
      skipSyncFromGridRef.current = false;
      return;
    }
    const { factorA, factorB, valueCol } = resolveTwoWayColumnNames(
      gridHeaders,
      factorAColumn,
      factorBColumn,
      valueColumn
    );
    const n = nPerCell;
    const fromGrid = gridDataToTwoWayBlockForm(gridData, gridHeaders, factorA, factorB, valueCol, n);
    if (fromGrid) {
      if (fromGrid.nPerCell !== nPerCell) {
        setNPerCellState(fromGrid.nPerCell);
      }
      setForm((prev) => (twoWayFormStateEqual(fromGrid, prev) ? prev : fromGrid));
      const { rowLabels: rL, columnLabels: cL } = getOrderedLevels(fromGrid.blocks);
      const na = clampKeisukeOneWayGroupCount(Math.max(2, cL.length));
      const nb = clampKeisukeOneWayGroupCount(Math.max(2, rL.length));
      if (na !== nLevelsA) onNLevelsAChange(na);
      if (nb !== nLevelsB) onNLevelsBChange(nb);
    }
    // nLevelsA / nLevelsB / nPerCell は依存に含めない（スピナー・未反映グリッドで戻さない）
  }, [gridData, gridHeaders, factorAColumn, factorBColumn, valueColumn, onNLevelsAChange, onNLevelsBChange]);

  const applyForm = useCallback(() => {
    const key = (b: string, a: string) => `${b}\t${a}`;
    const seen = new Set<string>();
    const uniqueBlocks = form.blocks.filter((b) => {
      const k = key(String(b.factorBLabel ?? ''), String(b.factorALabel ?? ''));
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const f: TwoWayBlockForm = { ...form, nPerCell, blocks: uniqueBlocks };
    const { headers, data } = twoWayBlockFormToGridData(f);
    suppressGridSyncUntilRef.current = Date.now() + 950;
    skipSyncFromGridRef.current = true;
    onApply(data, headers);
    onNPerCellChange?.(nPerCell);
  }, [form, nPerCell, onApply, onNPerCellChange]);

  useDebouncedFormApply(applyForm, [form, nPerCell, applyForm], {
    delayMs: 280,
    skipInitial: true,
  });

  const pushApplyFromForm = useCallback(
    (f: TwoWayBlockForm) => {
      const key = (b: string, a: string) => `${b}\t${a}`;
      const seen = new Set<string>();
      const uniqueBlocks = f.blocks.filter((b) => {
        const k = key(String(b.factorBLabel ?? ''), String(b.factorALabel ?? ''));
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      const merged: TwoWayBlockForm = { ...f, blocks: uniqueBlocks };
      const { headers, data } = twoWayBlockFormToGridData(merged);
      suppressGridSyncUntilRef.current = Date.now() + 950;
      skipSyncFromGridRef.current = true;
      onApply(data, headers);
      onNPerCellChange?.(merged.nPerCell);
    },
    [onApply, onNPerCellChange]
  );

  const onPasteDataCell = useCallback(
    (e: React.ClipboardEvent, r: number, aIdx: number) => {
      if (disabled) return;
      const text = clipboardPlainTextFromEvent(e);
      if (!clipboardLooksLikeSpreadsheetGrid(text)) return;
      e.preventDefault();
      const matrix = parseClipboardTsvMatrix(text);
      const anchor = getTwoWayDataPasteAnchor(gridDataSelection, { r, aIdx });
      setForm((prev) => {
        const next = mergeTwoWayDataPaste(prev, nPerCell, anchor.r, anchor.aIdx, matrix);
        const { rowLabels: rL, columnLabels: cL } = getOrderedLevels(next.blocks);
        const na = clampKeisukeOneWayGroupCount(Math.max(2, cL.length));
        const nb = clampKeisukeOneWayGroupCount(Math.max(2, rL.length));
        queueMicrotask(() => {
          if (na !== nLevelsA) onNLevelsAChange(na);
          if (nb !== nLevelsB) onNLevelsBChange(nb);
          pushApplyFromForm(next);
        });
        return next;
      });
    },
    [
      disabled,
      gridDataSelection,
      nPerCell,
      nLevelsA,
      nLevelsB,
      onNLevelsAChange,
      onNLevelsBChange,
      pushApplyFromForm,
    ]
  );

  const { rowLabels, columnLabels } = useMemo(() => getOrderedLevels(form.blocks), [form.blocks]);

  const n = nPerCell;
  /** 表示行数 = 因子B水準数 × 1セルあたりのデータ数 */
  const totalRows = rowLabels.length * n;

  const onTablePointerDownCapture = (e: React.PointerEvent<HTMLTableElement>) => {
    if (disabled || e.button !== 0) return;
    const cell = twoWayDataCellFromTarget(e.target);
    if (!cell) return;
    if (e.shiftKey && gridDataAnchorRef.current) {
      setGridDataSelection({ a: gridDataAnchorRef.current, b: cell });
      return;
    }
    gridDataAnchorRef.current = cell;
    setGridDataSelection({ a: cell, b: cell });
    setIsDataDragSelecting(true);
  };

  const onTableCopyCapture = (e: React.ClipboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    const t = e.target as HTMLElement;
    if (t.tagName !== 'INPUT') return;
    const focusedCell = twoWayDataCellFromTarget(t);
    const effectiveSel =
      gridDataSelection ??
      (focusedCell ? ({ a: focusedCell, b: focusedCell } satisfies TwoWayDataGridSelection) : null);
    if (!effectiveSel) return;
    const norm = normalizeTwoWayDataGridSelection(effectiveSel);
    const area = twoWayDataGridSelectionCellCount(norm);
    if (area <= 1) {
      const inp = t as HTMLInputElement;
      if (
        inp.selectionStart != null &&
        inp.selectionEnd != null &&
        inp.selectionStart !== inp.selectionEnd
      ) {
        return;
      }
    }
    e.preventDefault();
    e.stopPropagation();
    const tsv = buildTsvFromTwoWayDataSelection(form, rowLabels, columnLabels, n, norm);
    e.clipboardData?.setData('text/plain', tsv);
  };

  const onTableKeyDownCapture = (e: React.KeyboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setGridDataSelection(null);
      gridDataAnchorRef.current = null;
    }
  };

  const twoWayDataTdClass = (r: number, aIdx: number) =>
    [grid.twoWayDataCell, twoWayDataCellInSelection(r, aIdx, gridDataSelection) ? grid.tTestTwoColCellSelected : '']
      .filter(Boolean)
      .join(' ');

  const setNPerCell = (num: number) => {
    const v = Math.max(1, Math.min(50, num));
    setNPerCellState(v);
    setForm((prev) => ({
      ...prev,
      nPerCell: v,
      blocks: prev.blocks.map((b) => ({
        ...b,
        values: resizeArray(b.values, v),
      })),
    }));
  };

  /** 行ヘッダー（因子B）の変更: 同じ B 水準のブロックを一括で新しいラベルに */
  const setRowLabel = (bIdx: number, value: string) => {
    const oldLabel = rowLabels[bIdx];
    if (oldLabel === value) return;
    setForm((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.factorBLabel === oldLabel ? { ...b, factorBLabel: value } : b
      ),
    }));
  };

  /** 列ヘッダー（因子A）の変更: 同じ A 水準のブロックを一括で新しいラベルに */
  const setColumnLabel = (aIdx: number, value: string) => {
    const oldLabel = columnLabels[aIdx];
    if (oldLabel === value) return;
    setForm((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.factorALabel === oldLabel ? { ...b, factorALabel: value } : b
      ),
    }));
  };

  /** セル値の変更。表示行 r (0..totalRows-1)、表示列 c (0..columnLabels.length-1)。列0は行ヘッダーなので c=0 は行ラベル用 */
  const setCellValue = (r: number, c: number, value: string) => {
    const v = normalizeKeisukeNumericInput(value);
    const bIdx = Math.floor(r / n);
    const rep = r % n;
    const aIdx = c;
    const rowLabel = rowLabels[bIdx];
    const columnLabel = columnLabels[aIdx];
    const existing = getBlock(form.blocks, rowLabel, columnLabel);
    setForm((prev) => {
      let blocks = [...prev.blocks];
      if (existing) {
        blocks = blocks.map((b) => {
          if (b.factorBLabel === rowLabel && b.factorALabel === columnLabel) {
            const vals = [...b.values];
            vals[rep] = v;
            return { ...b, values: vals };
          }
          return b;
        });
      } else {
        const vals = Array(n).fill('');
        vals[rep] = v;
        blocks.push({
          factorALabel: columnLabel,
          factorBLabel: rowLabel,
          values: vals,
        });
      }
      return { ...prev, blocks };
    });
  };

  const setNLevelsA = (num: number) => {
    onNLevelsAChange(clampKeisukeOneWayGroupCount(num));
  };
  const setNLevelsB = (num: number) => {
    onNLevelsBChange(clampKeisukeOneWayGroupCount(num));
  };

  const clearTwoWayColResizeChrome = () => {
    document.body.style.removeProperty('user-select');
    document.body.style.removeProperty('cursor');
  };

  const onTwoWayColResizePointerDown =
    (kind: 'meta' | 'factorA') => (e: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      twoWayColDragRef.current = {
        kind,
        startX: e.clientX,
        startW: kind === 'meta' ? twoWayMetaColWidthPx : twoWayFactorAColWidthPx,
      };
    };

  const onTwoWayColResizePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = twoWayColDragRef.current;
    if (!sess) return;
    const dx = e.clientX - sess.startX;
    if (sess.kind === 'meta') {
      const nextW = Math.min(
        TWO_WAY_META_MAX_PX,
        Math.max(TWO_WAY_META_MIN_PX, Math.round(sess.startW + dx))
      );
      setTwoWayMetaColWidthPx(nextW);
    } else {
      setTwoWayFactorAColWidthPx(clampKeisukeDashboardDataColWidthPx(sess.startW + dx));
    }
  };

  const onTwoWayColResizePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = twoWayColDragRef.current;
    if (sess) {
      const dx = e.clientX - sess.startX;
      if (sess.kind === 'meta') {
        const finalW = Math.min(
          TWO_WAY_META_MAX_PX,
          Math.max(TWO_WAY_META_MIN_PX, Math.round(sess.startW + dx))
        );
        setTwoWayMetaColWidthPx(finalW);
      } else {
        const finalW = clampKeisukeDashboardDataColWidthPx(sess.startW + dx);
        setTwoWayFactorAColWidthPx(finalW);
        onLinkedDataColumnWidthCommit?.(finalW);
      }
    }
    twoWayColDragRef.current = null;
    clearTwoWayColResizeChrome();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  const onTwoWayColResizeLostCapture = () => {
    twoWayColDragRef.current = null;
    clearTwoWayColResizeChrome();
  };

  const aCount = columnLabels.length;
  const twoWayFactorAResizeHost = (aIdx: number) =>
    aCount === 1 || aIdx < aCount - 1 ? grid.keisukeTTestColResizeHost : undefined;

  return (
    <div className={`${styles.formWrap} ${styles.formWrapDense}`}>
      <p className={styles.formDescription}>
        {t('keisuke.dashboard.inputForm.twoWayBlocksDescription')}
      </p>
      <KeisukeDashboardGridObservationCountRow
        label={t('keisuke.dashboard.inputForm.nLevelsTwoWayA')}
        value={nLevelsA}
        onChange={setNLevelsA}
        onBlur={applyForm}
        disabled={disabled}
      />
      <KeisukeDashboardGridObservationCountRow
        label={t('keisuke.dashboard.inputForm.nLevelsTwoWayB')}
        value={nLevelsB}
        onChange={setNLevelsB}
        onBlur={applyForm}
        disabled={disabled}
      />
      <KeisukeDashboardGridObservationCountRow
        label={t('keisuke.dashboard.inputForm.nPerCell')}
        value={nPerCell}
        onChange={setNPerCell}
        onBlur={applyForm}
        disabled={disabled}
      />
      <div className={grid.keisukeAnovaTableWrap}>
        <div className={grid.tableScroll}>
          <table
            ref={tableRef}
            className={grid.keisukeAnovaGridTable}
            onPointerDownCapture={onTablePointerDownCapture}
            onCopyCapture={onTableCopyCapture}
            onKeyDownCapture={onTableKeyDownCapture}
          >
            <colgroup>
              <col
                className={grid.keisukeAnovaColMetaCorner}
                style={{ width: `${twoWayMetaColWidthPx}px` }}
              />
              {columnLabels.map((_, aIdx) => (
                <col
                  key={aIdx}
                  className={grid.keisukeAnovaColData}
                  style={{ width: `${twoWayFactorAColWidthPx}px` }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className={`${grid.twoWayMetaCell} ${grid.keisukeTTestColResizeHost}`}>
                  <input
                    type="text"
                    className={grid.keisukeAnovaGridInputMeta}
                    id={`${fieldIdPrefix}-tw-meta-fb`}
                    name={`${fieldIdPrefix}-tw-meta-fb`}
                    value={form.factorBColumn}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, factorBColumn: e.target.value }))
                    }
                    onBlur={applyForm}
                    disabled={disabled}
                    aria-label={t('keisuke.dashboard.inputForm.factorBColumnHeader')}
                  />
                  <button
                    type="button"
                    className={grid.keisukeTTestColResizeHandle}
                    data-tw-ignore-selection="1"
                    tabIndex={-1}
                    disabled={disabled}
                    aria-label={t('keisuke.dashboard.inputForm.resizeTwoWayRowHeaderColumn')}
                    onPointerDown={onTwoWayColResizePointerDown('meta')}
                    onPointerMove={onTwoWayColResizePointerMove}
                    onPointerUp={onTwoWayColResizePointerUp}
                    onPointerCancel={onTwoWayColResizePointerUp}
                    onLostPointerCapture={onTwoWayColResizeLostCapture}
                  />
                </th>
                {columnLabels.map((label, aIdx) => (
                  <th
                    key={aIdx}
                    className={[grid.twoWayMetaCell, twoWayFactorAResizeHost(aIdx)].filter(Boolean).join(' ')}
                  >
                    <input
                      type="text"
                      className={grid.keisukeAnovaGridInputMeta}
                      id={`${fieldIdPrefix}-tw-h-${aIdx}`}
                      name={`${fieldIdPrefix}-tw-h-${aIdx}`}
                      value={label}
                      onChange={(e) => setColumnLabel(aIdx, e.target.value)}
                      onBlur={applyForm}
                      disabled={disabled}
                      aria-label={t('keisuke.dashboard.inputForm.groupLabel', { num: aIdx + 1 })}
                    />
                    {(aCount === 1 || aIdx < aCount - 1) && (
                      <button
                        type="button"
                        className={grid.keisukeTTestColResizeHandle}
                        data-tw-ignore-selection="1"
                        tabIndex={-1}
                        disabled={disabled}
                        aria-label={t('keisuke.dashboard.inputForm.resizeTwoWayFactorAColumnsUniform')}
                        onPointerDown={onTwoWayColResizePointerDown('factorA')}
                        onPointerMove={onTwoWayColResizePointerMove}
                        onPointerUp={onTwoWayColResizePointerUp}
                        onPointerCancel={onTwoWayColResizePointerUp}
                        onLostPointerCapture={onTwoWayColResizeLostCapture}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: totalRows }, (_, r) => {
                const bIdx = Math.floor(r / n);
                const rep = r % n;
                const rowLabel = rowLabels[bIdx];
                return (
                  <tr key={r}>
                    <td className={grid.twoWayMetaCell}>
                      <input
                        type="text"
                        className={grid.keisukeAnovaGridInputMeta}
                        id={`${fieldIdPrefix}-tw-rl-${bIdx}`}
                        name={`${fieldIdPrefix}-tw-rl-${bIdx}`}
                        value={rowLabel}
                        onChange={(e) => setRowLabel(bIdx, e.target.value)}
                        onBlur={applyForm}
                        disabled={disabled}
                        aria-label={t('keisuke.dashboard.inputForm.groupLabel', { num: bIdx + 1 })}
                      />
                    </td>
                    {columnLabels.map((colLabel, aIdx) => {
                      const block = getBlock(form.blocks, rowLabel, colLabel);
                      const val = block?.values[rep] ?? '';
                      return (
                        <td key={aIdx} className={twoWayDataTdClass(r, aIdx)}>
                          <input
                            type="text"
                            className={grid.keisukeAnovaGridInputCell}
                            id={`${fieldIdPrefix}-tw-r${r}-a${aIdx}`}
                            name={`${fieldIdPrefix}-tw-r${r}-a${aIdx}`}
                            data-tw-r={r}
                            data-tw-a={aIdx}
                            value={val}
                            onChange={(e) => setCellValue(r, aIdx, e.target.value)}
                            onBlur={applyForm}
                            onPaste={(e) => onPasteDataCell(e, r, aIdx)}
                            inputMode="decimal"
                            disabled={disabled}
                            aria-label={t('keisuke.dashboard.inputForm.cell', {
                              row: r + 1,
                              col: aIdx + 1,
                            })}
                          />
                        </td>
                    );
                  })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {totalRows === 0 && (
        <p className="small text-muted mb-2">
          {t('keisuke.dashboard.inputForm.twoWayBlocksEmpty')}
        </p>
      )}
      <KeisukeClipboardHintBar />
    </div>
  );
}
